import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAccountStore } from '../../components/Stores/accountStore';
import { GENERAL_ID, useExposureStore } from '../../components/Stores/exposureDataStore';
import { useExposure } from '../../hooks/useExposure';
import { getExposureCardData, getMaterialToAccountById } from '../Dashboard/dashboardUtils';
import { ExposureCard, IExposureCardProps } from '../Dashboard/ExposureCard';
import { useMaterialsStore } from '../../components/Stores/MaterialsStore';
import { ChevronLeft } from '@mui/icons-material';
import { Material } from '../../sp-types';
import { getNetExposure } from '../../utils/exposureUtils';
import { balanceMaterial } from '../BalanceFlow/balanceUtils';
import { useCurrencyRates } from '@hooks/useCurrencyRates';

export const ExposureOverview: React.FC = () => {
  const { account, materialColorDictionary: colorsMap } = useAccountStore();
  const navigate = useNavigate();
  const { materialsMap } = useMaterialsStore();
  const { refetch: refetchExposure } = useExposure(account.id);
  const [isLoading, setIsLoading] = React.useState(false);
  const { netExposure, isNetExposureLoading } = useExposureStore();
  const { convertionMap } = useCurrencyRates();

  React.useEffect(() => {
    if (account?.id) {
      console.debug('fetc exp');
      refetchExposure();
    }
  }, [account?.id]);

  const getMaterial = ({ materialId }: { materialId: string }): Material => materialsMap[materialId];

  const balanceNow = async ({ materialId }: { materialId: string }) => {
    setIsLoading(true);
    const balanceId = await balanceMaterial(
      materialId,
      account.id || '',
      getNetExposure(netExposure, convertionMap, materialId),
      netExposure.materialsExposure[materialId],
    );

    setIsLoading(false);
    if (balanceId) {
      navigate(`/balance-risk/${balanceId}`);
    }
  };

  const isUnbalanced = ({ netExposure, gaugeBarProps: { riskTolerance } }: IExposureCardProps): boolean =>
    netExposure > riskTolerance.stableArea.end || netExposure < riskTolerance.stableArea.start;

  return (
    <div className="flex flex-col gap-4 p-3 h-full overflow-hidden">
      <Link to={'..'} className="max-h-[5%]">
        <ChevronLeft />
        Back to dashboard
      </Link>
      <div>
        {!isNetExposureLoading ? (
          <div className="border-l-8 border-transparent">
            <ExposureCard
              {...getExposureCardData(
                netExposure,
                getMaterialToAccountById(account, 'general'),
                account.riskPercents,
                convertionMap,
                materialsMap,
                materialsMap[GENERAL_ID],
              )}
              onBalanceNowClickAction={() => 0}
              isBalanceNowLoading={isLoading}
              isExposureLoading={false}
              mainTitle="General exposure"
              balanceButton="hide"
            />
          </div>
        ) : (
          <div className="flex flex-col grow w-full min-h-36">
            <div className="flex flex-grow animate-pulse bg-white"></div>
          </div>
        )}
      </div>
      <div className="flex flex-col gap-2 max-h-[75%]">
        <p className="text-lg text-red">Materials</p>
        {materialsMap && !isNetExposureLoading ? (
          <div className="flex-1 overflow-y-auto scroll-strong">
            <div className="flex flex-col gap-3">
              {account?.materialsToAccount
                ?.filter(({ materialId }) => materialId !== GENERAL_ID)
                .map((material) => ({
                  material,
                  cardData: getExposureCardData(
                    netExposure,
                    material,
                    account.riskPercents,
                    convertionMap,
                    getMaterial(material),
                  ),
                }))
                .filter(({ cardData }) => isUnbalanced(cardData))
                .map(({ material, cardData }, index) => (
                  <div key={index} className="border-l-8" style={{ borderColor: colorsMap[material.materialId] }}>
                    <ExposureCard
                      {...cardData}
                      onBalanceNowClickAction={() => balanceNow(material)}
                      mainTitle={materialsMap[material.materialId]?.name + ' exposure'}
                      isBalanceNowLoading={isLoading}
                      isExposureLoading={false}
                      balanceButton="right"
                    />
                  </div>
                ))}
            </div>
          </div>
        ) : (
          <div className="flex flex-col grow w-full min-h-36 gap-3">
            {[1, 2, 4].map((key) => (
              <div key={key} className="flex flex-grow animate-pulse bg-white min-h-32"></div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};
